import React, { useEffect, useMemo, useState, useCallback } from "react";
import DatePicker from "react-datepicker";
import fi from "date-fns/locale/fi";
import moment from "moment";
import { useParams } from "react-router-dom";
import { useForm } from "../../hooks";
import * as store from "../../framework/customStore";
import { handleApiError } from "../../models/store/storeEffects";
import { Translations } from "../../models/translations";
import { StrMeasureUnit } from "../../models/common/enums";

import { getDayBookingTypes } from "../../services/dayBookingTypeService";

import { IWorkTimeDetailsPage } from "./workTimeBetaDetailsPageMain";
import { DayBookingItemBeta } from "../../models/dayBooking/dayBookingItemBeta";
import { DayBookingTypeItemBeta } from "../../models/dayBookingType/dayBookingTypeItemBeta";

import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Unstable_Grid2";
import HorizontalRule from "@mui/icons-material/HorizontalRule";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import { Base } from "../../framework/base";
import { SubmitButton } from "../framework/form";
import MuiAutocomplete, { IMuiSelectOption } from "../framework/muiAutocomplete2";
import { showErrorMessage } from "../../models/store/storeActions";
import { useTranslation } from "react-i18next";
import { usePrompt } from "../../hooks/usePrompt";
import { getDayBookingDuration, saveDayBooking } from "../../services/dayBookingService";
import { DayBookingSaveData } from "../../models/dayBooking/dayBookingSaveData";
import { SalaryRowTypeKindCode } from "../../models/salary/salaryRowTypeItem";

interface IDayBookingTypeSelectOption extends DayBookingTypeItemBeta {
    label: string;
    value: string;
}

interface IDayBookingFormItem {
  dayBookingType: IDayBookingTypeSelectOption;
  costCenter: IMuiSelectOption;
  vehicle: IMuiSelectOption;
  startTime: Date;
  endTime: Date;
  description: string;
}

interface IWorkTimeDetailsDayBookingFormProps {
    afterSave: () => void;
    editDayBookingItem: DayBookingItemBeta;
    setEditDayBookingItem: React.Dispatch<
        React.SetStateAction<DayBookingItemBeta>
    >;
    costCenterId: string | undefined;
    vehicleId: string | undefined;
}

const initEmptyDayBooking = (date: string): IDayBookingFormItem => {
    return {
        dayBookingType: null,
        costCenter: null,
        vehicle: null,
        startTime: new Date(date),
        endTime: new Date(date),
        description: "",
    };
};

const initDayBooking = (
    item: DayBookingItemBeta,
    dayBookingTypes: DayBookingTypeItemBeta[],
): IDayBookingFormItem => {
    const dayBookingType: DayBookingTypeItemBeta = dayBookingTypes?.find(
        (t) => t.id === item.dayBookingTypeId
    );
    return {
        dayBookingType: dayBookingType
            ? {
                ...dayBookingType,
                label: dayBookingType.name,
                value: dayBookingType.id,
            }
            : null,
        costCenter: item.costCenterId
            ? {
                value: item.costCenterId,
                label: item.costCenterName,
            }
            : null,
        vehicle: item.vehicleId
            ? {
                value: item.vehicleId,
                label: item.vehicleName,
            }
            : null,
        startTime: new Date(item.day),
        endTime: new Date(item.getEndDay()),
        description: item.comment || "",
    };
};

const measureUnits = {
    days: [StrMeasureUnit.Day, StrMeasureUnit.DayFiAlt, StrMeasureUnit.DayEn],
    hours: [StrMeasureUnit.Hour, StrMeasureUnit.HourFi],
    amount: [StrMeasureUnit.Piece]
};

const getDurationFieldLabel = ({ measureUnitIsDays, measureUnitIsHours, measureUnitIsAmount }, isPartialDayBooking: boolean) => {
    let prefix = Translations.Duration;
    let suffix = Translations.AbrDays;
    if(isPartialDayBooking) suffix = Translations.AbrHours;
    if(measureUnitIsDays && !isPartialDayBooking) suffix = Translations.AbrDays;
    if(measureUnitIsHours) suffix = Translations.AbrHours;
    if(measureUnitIsAmount) {
        prefix = Translations.Amount;
        suffix = Translations.MeasureUnitPcs;
    }
    return `${prefix} (${suffix})`;
};

export function WorkTimeDetailsDayBookingForm({
    afterSave,
    editDayBookingItem,
    setEditDayBookingItem,
    costCenterId,
    vehicleId,
}: IWorkTimeDetailsDayBookingFormProps) {
    const { employeeId, date } = useParams<IWorkTimeDetailsPage>();
    const [edited, setEdited] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const theme = useTheme();
    const [dayBooking, setDayBooking] = useState<IDayBookingFormItem>();
    const [dayBookingTypes, setDayBookingTypes] = useState<DayBookingTypeItemBeta[]>();
    const [amount, setAmount] = useState<number>(0);
    const [calculatedDuration, setCalculatedDuration] = useState<number>();
    const [optionalDataDisallowed, setOptionalDataDisallowed] = useState<boolean>(false);
    const costCenters = store.useAppSelector(state => state.costCenter.groupedOptions);
    const vehicleOptions = store.useAppSelector(state => state.vehicle.options);

    const { dayBookingType, costCenter, vehicle, startTime, endTime, description } = dayBooking || {};
    let allowEntryInDays = dayBookingType?.allowEntryInDays ?? true;
    const allowEntryInHours = dayBookingType?.allowEntryInHours ?? true;

    const measureUnit = dayBookingType?.salaryRowTypeMeasureUnit;
    const measureUnitIsDays = measureUnits.days.some(unit => unit === measureUnit);
    allowEntryInDays = allowEntryInDays || measureUnitIsDays;
    const measureUnitIsHours = measureUnits.hours.some(unit => unit === measureUnit);
    const measureUnitIsAmount = measureUnits.amount.some(unit => unit === measureUnit);

    const isPartialDayBooking = !measureUnitIsDays &&
        !(!allowEntryInHours && allowEntryInDays && !measureUnitIsHours && !measureUnitIsAmount);

    const showTimeInput =
        measureUnitIsHours || (isPartialDayBooking && !measureUnitIsAmount);

    const days: string = startTime && endTime
        ? (Base.dateDiffInDays(startTime, endTime) + 1).toString()
        : "";
    const duration = allowEntryInDays && measureUnitIsDays
        ? days
        : amount;
    const durationFieldLabel = getDurationFieldLabel({ measureUnitIsDays, measureUnitIsHours, measureUnitIsAmount }, isPartialDayBooking);
    const submitDisabled = Base.isNullOrUndefined(dayBookingType)
      || Base.isNullOrUndefined(startTime)
      || Base.isNullOrUndefined(endTime)
      || (startTime && endTime && moment(endTime).isBefore(startTime));

    const clearDisabled = !dayBookingType && !description;
    const { t } = useTranslation();

    const afterSaveForm = () => {
        clearForm();
        afterSave();
    };

    const clearForm = () => {
        setEditDayBookingItem(null);
        setDayBooking(initEmptyDayBooking(date));
        setEdited(false);
    };

    useEffect(() => {
        if (!showTimeInput) {
            setAmount(Math.floor(amount));
        }
    }, [showTimeInput]);

    const fetchDayBookingDuration = async() => {
        const dbDuration = await getDayBookingDuration({
            dayBookingTypeId: dayBookingType?.value ?? "",
            day: startTime ? Base.dayjsToJsonDate(startTime) : "",
            amount: Base.dateDiffInDays(startTime, endTime) + 1,
        });

        setCalculatedDuration(dbDuration);
    };

    useEffect(() => {
        if (
            startTime &&
            endTime &&
            allowEntryInDays &&
            measureUnitIsDays &&
            dayBookingType?.salaryRowTypeKindCode ==
                SalaryRowTypeKindCode.Vacation
        ) {
            void fetchDayBookingDuration();
        } else {
            setCalculatedDuration(null);
        }
    }, [
        startTime,
        endTime,
        allowEntryInDays,
        measureUnitIsDays,
        dayBookingType?.salaryRowTypeKindCode,
    ]);

    const [formRef, submitting, submitForm] = useForm(
        saveDayBooking,
        afterSaveForm,
        useMemo(
            () =>
                ({
                    id: editDayBookingItem?.id ?? "",
                    employeeId: employeeId,
                    dayBookingTypeId: dayBookingType?.value ?? "",
                    day: startTime ? Base.dayjsToJsonDate(startTime) : "",
                    amount: duration,
                    dayUsageType: isPartialDayBooking ? 1 : 0,
                    comment: description,
                    rowId: editDayBookingItem?.rowId ?? "",
                    automaticCategory:
                        editDayBookingItem?.automaticCategory ?? "",
                    automaticWorkShiftTimeSlotId:
                        editDayBookingItem?.automaticWorkShiftTimeSlotId ?? "",
                } as DayBookingSaveData),
            [
                employeeId,
                editDayBookingItem?.id,
                editDayBookingItem?.rowId,
                dayBookingType?.value,
                startTime,
                duration,
                isPartialDayBooking,
                description,
            ]
        ),
        DayBookingSaveData,
        t("dayBooking.dayBookingSaveSuccessful")
    );

    const fetchDayBookingTypes = useCallback(async() => {
        setIsLoading(true);
        const results = await getDayBookingTypes();
        setDayBookingTypes(results);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        // Vacations and absences can't have cost center or vehicle
        if (dayBookingType && dayBookingType.salaryRowTypeMeasureUnit !== null &&
            (
                dayBookingType.salaryRowTypeKindCode === SalaryRowTypeKindCode.Absence.toString() ||
                dayBookingType.salaryRowTypeKindCode === SalaryRowTypeKindCode.Vacation.toString()
            )
        ) {
            setOptionalDataDisallowed(true);
            updateDayBooking("costCenter", null);
            updateDayBooking("vehicle", null);
        } else {
            setOptionalDataDisallowed(false);
        }
    }, [dayBookingType]);

    useEffect(() => {
        fetchDayBookingTypes()
            .catch((e) => handleApiError(e, store.customStore.dispatch));
    }, [fetchDayBookingTypes]);

    useEffect(() => {
        if (date && employeeId && dayBookingTypes) {
            setIsLoading(false);

            if (editDayBookingItem?.employeeId === employeeId) {
                setEdited(false);
                setDayBooking(
                    initDayBooking(
                        editDayBookingItem,
                        dayBookingTypes
                    )
                );
            } else {
                clearForm();
            }

            if (editDayBookingItem?.amount) {
                setAmount(editDayBookingItem.amount);
            } else {
                setAmount(0);
            }
        }
    }, [dayBookingTypes, date, employeeId, editDayBookingItem]);

    const handleSubmit = async() => {
        if (submitDisabled) {
            store.customStore.dispatch(
                showErrorMessage(t("unableToSaveChanges"))
            );
            return false;
        }

        return await submitForm();
    };

    const updateDayBooking = (field: string, value: any) => {
        setEdited(true);
        setDayBooking({ ...dayBooking, [field]: value });
    };

    function updateAmountHours(hours) {
        let minutes = null;
        if (!hours) {
            hours = 0;
        }
        if (Number.isInteger(amount)) {
            minutes = 0;
        } else {
            minutes = amount % 1;
        }
        setAmount(hours + minutes);
    }

    function updateAmountMinutes(minutes) {
        if (!minutes) {
            minutes = 0;
        }
        if (minutes < 60) {
            const hours = Math.trunc(amount);
            minutes = (minutes / 60);
            setAmount(hours + minutes);
        }
    }

    // Update costcenter and vehicle based on daybooking type,
    // when creating a new day booking
    useEffect(() => {
        if (!editDayBookingItem?.id && !!dayBookingType) {
            let newCostCenter = null;
            let newVehicle = null;

            if (
                dayBookingType.useWorkShiftCostCenter &&
                !!costCenterId &&
                !costCenter
            ) {
                newCostCenter = costCenters.find(
                    (c) => c.value === costCenterId
                );
            }

            if (dayBookingType.useWorkShiftVehicle && !!vehicleId && !vehicle) {
                newVehicle = vehicleOptions.find((c) => c.value === vehicleId);
            }

            if (!!newCostCenter || !!newVehicle) {
                setDayBooking({
                    ...dayBooking,
                    vehicle: newVehicle,
                    costCenter: newCostCenter,
                });
            }
        }
    }, [editDayBookingItem?.id, dayBookingType, costCenterId, vehicleId]);

    usePrompt(edited, handleSubmit);

    return isLoading ? null : (
        <form ref={formRef}>
            <Typography variant="h6" mb={1}>
                {editDayBookingItem ? Translations.Edit : Translations.AddNew}
            </Typography>
            <Grid2 container spacing={2}>
                <Grid2 xs={12} maxWidth={600}>
                    <MuiAutocomplete
                        label={Translations.DayBookingType}
                        options={dayBookingTypes.map((i) => ({
                            ...i,
                            label: i?.salaryRowTypeMeasureUnit
                                ? `${i.name} (${i.salaryRowTypeMeasureUnit})`
                                : i.name,
                            value: i.id,
                        }))}
                        value={dayBookingType}
                        onChange={(val) =>
                            updateDayBooking("dayBookingType", val)
                        }
                        required
                        autocompleteProps={{ sx: { backgroundColor: "white" } }}
                    />
                </Grid2>
                <Grid2 xs={12} maxWidth={600}>
                    <MuiAutocomplete
                        label={Translations.CostCenter}
                        options={costCenters}
                        name="costCenterId"
                        value={costCenter}
                        onChange={(val) =>
                            updateDayBooking("costCenter", val)
                        }
                        autocompleteProps={{ sx: { backgroundColor: "white" } }}
                        disabled={optionalDataDisallowed}
                    />
                </Grid2>
                <Grid2 xs={12} maxWidth={600}>
                    <MuiAutocomplete
                        label={Translations.Vehicle}
                        options={vehicleOptions}
                        name="vehicleId"
                        value={vehicle}
                        onChange={(val) =>
                            updateDayBooking("vehicle", val)
                        }
                        autocompleteProps={{ sx: { backgroundColor: "white" } }}
                        disabled={optionalDataDisallowed}
                    />
                </Grid2>
                <Grid2 container xs={24} columns={24} maxWidth={600}>
                    <Grid2 xs={23} lg={9} maxWidth={210}>
                        <DatePicker
                            dateFormat="dd.MM.yyyy"
                            locale={fi} // TODO: get from appConfig
                            selected={startTime}
                            onChange={(val: Date) =>
                                updateDayBooking("startTime", val)
                            }
                            showWeekNumbers
                            required
                            popperClassName={"z-3"}
                            customInput={
                                <TextField
                                    label={
                                        !isPartialDayBooking
                                            ? Translations.StartTime
                                            : Translations.Date
                                    }
                                    sx={{ backgroundColor: "white" }}
                                />
                            }
                        />
                    </Grid2>
                    <Grid2
                        xs={false}
                        lg={2}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        hidden={isPartialDayBooking}
                    >
                        <HorizontalRule />
                    </Grid2>
                    <Grid2 xs={23} lg={9} maxWidth={210} hidden={isPartialDayBooking}>
                        <DatePicker
                            dateFormat="dd.MM.yyyy"
                            locale={fi} // TODO: get from appConfig
                            selected={endTime}
                            onChange={(val: Date) =>
                                updateDayBooking("endTime", val)
                            }
                            showWeekNumbers
                            required
                            popperClassName={"z-3"}
                            customInput={
                                <TextField
                                    label={Translations.EndTime}
                                    error={endTime < startTime}
                                    sx={{ backgroundColor: "white" }}
                                />
                            }
                        />
                    </Grid2>
                    {showTimeInput ? (
                        <>
                            <Grid2 xs={14} lg={6} maxWidth={104}>
                                <TextField
                                    label={t("units.hours")}
                                    value={Math.trunc(amount).toString()}
                                    type={"number"}
                                    InputProps={{
                                        inputProps: {
                                            min: 0,
                                            max: 1000,
                                        },
                                        required: true,
                                    }}
                                    onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>
                                    ) => {
                                        setEdited(true);
                                        updateAmountHours(parseInt(e?.target?.value));
                                    }}
                                    sx={{
                                        backgroundColor: "white",
                                        ".MuiInputLabel-root": { overflow: "visible" },
                                    }}
                                />
                            </Grid2>
                            <Grid2 xs={14} lg={6} maxWidth={104}>
                                <TextField
                                    label={t("units.minutes")}
                                    value={Base.hoursToMinutes(amount % 1).toString()}
                                    type={"number"}
                                    InputProps={{
                                        inputProps: {
                                            min: 0,
                                            max: 59,
                                        },
                                        required: true,
                                    }}
                                    onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>
                                    ) => {
                                        setEdited(true);
                                        updateAmountMinutes(parseInt(e?.target?.value));
                                    }}
                                    sx={{
                                        backgroundColor: "white",
                                        ".MuiInputLabel-root": { overflow: "visible" },
                                    }}
                                />
                            </Grid2>
                        </>
                    ) : (
                        <Grid2 xs={14} lg={6} maxWidth={104}>
                            <TextField
                                label={durationFieldLabel}
                                value={calculatedDuration ?? duration}
                                type={"number"}
                                InputProps={{
                                    readOnly: !isPartialDayBooking,
                                    inputProps: {
                                        min: 0,
                                        max: measureUnitIsHours ? 23 : null,
                                    },
                                    required: true,
                                }}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                    setEdited(true);
                                    setAmount(parseInt(e?.target?.value));
                                }}
                                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                                    if (measureUnitIsHours)
                                        setAmount(
                                            Math.min(parseInt(e?.target?.value), 23)
                                        );
                                }}
                                sx={{
                                    backgroundColor: "white",
                                    ".MuiInputLabel-root": { overflow: "visible" },
                                }}
                            />
                        </Grid2>
                    )}
                </Grid2>
                <Grid2 xs={24} maxWidth={600}>
                    <TextField
                        key={editDayBookingItem?.id}
                        label={Translations.Description}
                        value={description}
                        onChange={(e) =>
                            updateDayBooking("description", e.target.value)
                        }
                        multiline
                        minRows={2}
                        fullWidth
                        sx={{ backgroundColor: "white" }}
                    />
                </Grid2>
                <Grid2 xs={12}>
                    <SubmitButton
                        submitting={submitting}
                        disabled={submitDisabled}
                        color="secondary"
                        sx={{
                            float: "right",
                            color: theme.palette.common.white,
                        }}
                    />
                    <Button
                        variant="outlined"
                        disabled={clearDisabled}
                        color="secondary"
                        sx={{
                            float: "right",
                            marginRight: "1rem",
                        }}
                        onClick={clearForm}
                    >
                        {Translations.Clear}
                    </Button>
                </Grid2>
            </Grid2>
        </form>
    );
}
